import {
  Alert,
  Button,
  Choice,
  DataStatus,
  Dropdown,
  DropdownItem,
  Separator,
} from 'platform/components';
import {Box, HStack, Heading, Hide, Scroll, Show, Spinner, VStack} from 'platform/foundation';

import {useEffect, useRef} from 'react';

import {defaultTo} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {
  Basket,
  BasketMechanic,
  BasketSummary,
  EitherQuantityOrError,
  getOptionsFromMechanics,
  MappedBasketItem,
  useBasketItems,
} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {AfterSalesLabourBasketItem} from './components/AfterSalesLabourBasketItem';
import {LabourBasketItem} from './types/LabourBasketItem';
import {LabourBasketItemEditingDetails} from './types/LabourBasketItemEditingDetails';

const BASKET_TITLE_HEIGHT_IN_REM = 3.5;

type BulkAction<T extends LabourBasketItem> = {
  label: string;
  action: (basketItems: MappedBasketItem<T>[]) => void;
};

export interface AfterSalesLabourBasketProps<
  T extends LabourBasketItem,
> extends RequiredTestIdProps {
  basket: Basket<T>;
  mechanic: BasketMechanic;
  onQuantityChange: (itemId: string, quantity: EitherQuantityOrError) => Promise<void>;
  onEdit: (editingDetails: LabourBasketItemEditingDetails) => void;
  onDelete: (itemId: string) => Promise<void>;
  bulkActions?: BulkAction<T>[];
  focusTrigger?: number;
  onTabOut?: () => void;
}

export function AfterSalesLabourBasket<T extends LabourBasketItem>(
  props: AfterSalesLabourBasketProps<T>
) {
  const {
    basketItems,
    basketItemsCount,
    hasSomeBasketItemsSelected,
    hasAllBasketItemsSelected,
    onSelectBasketItem,
    onSelectAllBasketItems,
  } = useBasketItems({basketItems: props.basket?.items});

  const basketTitle = `${i18n.t('general.labels.addedItems')} (${basketItemsCount})`;

  const basketTotalPrice = props.basket?.totalPrice;

  // ── Auto-focus the last basket item's quantity input after an item is added ──
  //
  // Parent increments `focusTrigger` when an add-to-basket action resolves.
  // Two scenarios:
  //   A. hasPendingRequest is already false (e.g. first item, no basket refetch):
  //      the effect runs on the focusTrigger change and focuses immediately.
  //   B. hasPendingRequest is still true (basket refetch in flight):
  //      the intent is stored in pendingFocusRef; the effect waits for the
  //      hasPendingRequest true→false transition before focusing.
  const scrollRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef(false);
  const prevFocusTriggerRef = useRef(props.focusTrigger);
  const focusedItemIdRef = useRef<string | null>(null);

  // useEffect justified: reacts to an async prop transition (hasPendingRequest true→false)
  // that cannot be observed via an event handler — the DOM element only exists after render.
  // Also depends on basketItemsCount so it re-runs when new items appear in the DOM
  // (covers the case where hasPendingRequest settles before the query refetch delivers items).
  useEffect(
    function focusLastItemOnTrigger() {
      if (props.focusTrigger !== prevFocusTriggerRef.current) {
        pendingFocusRef.current = true;
        prevFocusTriggerRef.current = props.focusTrigger;
      }

      if (!pendingFocusRef.current || props.basket.hasPendingRequest || !scrollRef.current) {
        return;
      }

      const items = scrollRef.current.querySelectorAll<HTMLElement>('[data-item-id]');
      const lastItem = items[items.length - 1];

      if (!lastItem) {
        return;
      }

      pendingFocusRef.current = false;
      lastItem.scrollIntoView({behavior: 'smooth', block: 'nearest'});

      // The wrapper div ([data-item-id]) renders before its child NumberInput mounts,
      // so we observe the subtree until the quantity input appears, then focus it.
      const input = lastItem.querySelector<HTMLInputElement>(
        '[data-testid$="quantity-numberInput-input"]'
      );

      // Focus the input and select its value after a rAF so the selection
      // survives NumberInput's value-sync effect (which re-renders and
      // deselects on mount). Returns a cleanup that cancels the pending rAF.
      let rafId: number | undefined;
      const focusAndSelect = (el: HTMLInputElement) => {
        el.focus();
        rafId = requestAnimationFrame(() => {
          el.focus();
          el.select();
        });
      };

      if (input) {
        focusAndSelect(input);
        return () => cancelAnimationFrame(rafId!);
      }

      const observer = new MutationObserver(() => {
        const found = lastItem.querySelector<HTMLInputElement>(
          '[data-testid$="quantity-numberInput-input"]'
        );
        if (found) {
          observer.disconnect();
          focusAndSelect(found);
        }
      });
      observer.observe(lastItem, {childList: true, subtree: true});

      return () => {
        observer.disconnect();
        if (rafId !== undefined) {
          cancelAnimationFrame(rafId);
        }
      };
    },
    [props.focusTrigger, props.basket.hasPendingRequest, basketItemsCount]
  );

  // ── Preserve focus across basket refetches ──
  //
  // When a quantity change triggers a basket refetch (hasPendingRequest goes true),
  // capture which item's input is focused. When the refetch finishes (goes false),
  // restore focus to that same item's input.
  // useEffect justified: DOM event subscription on the scroll container with cleanup.
  useEffect(function trackFocusedQuantityInput() {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.matches('[data-testid$="quantity-numberInput-input"]')) {
        focusedItemIdRef.current =
          target.closest<HTMLElement>('[data-item-id]')?.dataset.itemId ?? null;
      } else {
        focusedItemIdRef.current = null;
      }
    };

    const onFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLInputElement | null;
      if (target?.disabled) {
        return;
      }

      const related = e.relatedTarget as Node | null;
      if (!related || !container.contains(related)) {
        focusedItemIdRef.current = null;
      }
    };

    container.addEventListener('focusin', onFocusIn);
    container.addEventListener('focusout', onFocusOut);
    return () => {
      container.removeEventListener('focusin', onFocusIn);
      container.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  // Restore focus after a basket refetch re-enables the input.
  // useEffect justified: must run after React commits the re-enabled input to the DOM.
  useEffect(
    function restoreQuantityInputFocus() {
      if (props.basket.hasPendingRequest || !focusedItemIdRef.current || !scrollRef.current) {
        return;
      }

      const itemId = focusedItemIdRef.current;

      const rafId = requestAnimationFrame(() => {
        const itemEl = scrollRef.current?.querySelector<HTMLElement>(
          `[data-item-id="${CSS.escape(itemId)}"]`
        );
        const input = itemEl?.querySelector<HTMLInputElement>(
          '[data-testid$="quantity-numberInput-input"]'
        );
        input?.focus();
      });

      return () => cancelAnimationFrame(rafId);
    },
    [props.basket.hasPendingRequest]
  );

  return (
    <Box
      borderRadius="small"
      boxShadow="elevation_1"
      backgroundColor="palettes.white.10.100"
      height="100%"
      data-testid={suffixTestId('wrapper', props)}
    >
      <VStack height="100%">
        <Box padding={4}>
          <HStack spacing={2} align="center">
            <Heading size={4} data-testid={suffixTestId('basketTitle', props)}>
              {basketTitle}
            </Heading>
            <Show when={props.basket.hasPendingRequest}>
              <Spinner size="small" data-testid={suffixTestId('pendingRequestSpinner', props)} />
            </Show>
          </HStack>
        </Box>
        <Separator spacing={0} />
        <Box padding={4} height="100%" maxHeight={`calc(100% - ${BASKET_TITLE_HEIGHT_IN_REM}rem)`}>
          <VStack height="100%" spacing={3}>
            <Choice
              isRequired={props.mechanic?.isMechanicRequired}
              isLoading={props.mechanic?.areMechanicsLoading}
              isDisabled={props.basket.hasPendingRequest}
              label={i18n.t('entity.order.labels.mechanic')}
              value={defaultTo('', props.mechanic?.selectedMechanicId)}
              options={defaultTo([], getOptionsFromMechanics(props.mechanic?.mechanics))}
              onChange={props.mechanic?.onMechanicChange}
              errorMessage={props.mechanic?.mechanicError}
              data-testid={suffixTestId('assignedMechanic', props)}
            />

            <Show when={props.basket.hasInvalidItems}>
              <Alert
                type="inline"
                variant="error"
                title={i18n.t('general.notifications.basketHasInvalidItems')}
                data-testid={suffixTestId('alert.hasInvalidItems', props)}
              />
            </Show>

            <HStack justify="space-between">
              <Hide when={isNilOrEmpty(basketItems)}>
                <Button
                  isDisabled={props.basket.hasPendingRequest}
                  type="button"
                  variant="link"
                  leftIcon="action/done_all"
                  title={
                    hasAllBasketItemsSelected
                      ? i18n.t('general.actions.unselectAll')
                      : i18n.t('general.actions.selectAll')
                  }
                  onClick={onSelectAllBasketItems}
                  data-testid={suffixTestId('actions.toggleSelect', props)}
                />
              </Hide>

              <Show when={hasSomeBasketItemsSelected && isNotNilOrEmpty(props.bulkActions)}>
                <Dropdown
                  closeOnBlur
                  closeOnSelect
                  dropdownControl={
                    <Button
                      isDisabled={props.basket.hasPendingRequest}
                      type="button"
                      variant="link"
                      leftIcon="action/reorder"
                      title={i18n.t('general.actions.bulkActions')}
                      data-testid={suffixTestId('buttonSelectBulkAction', props)}
                    />
                  }
                  data-testid={suffixTestId('selectBulkAction', props)}
                >
                  {props.bulkActions?.map(({action, label}) => (
                    <DropdownItem
                      key={label}
                      label={label}
                      onClick={() =>
                        basketItems &&
                        action(basketItems.filter((basketItem) => basketItem.isSelected))
                      }
                    />
                  ))}
                </Dropdown>
              </Show>
            </HStack>

            <Scroll ref={scrollRef} flex={1} auto>
              <VStack height="100%" spacing={4}>
                <DataStatus
                  isLoading={props.basket.isLoading}
                  isError={props.basket.hasError}
                  isEmpty={basketItemsCount === 0}
                  emptyMessage={i18n.t('general.notifications.noItemsSelected')}
                  minHeight="100%"
                >
                  {basketItems?.map((basketItem, index) => (
                    <div key={basketItem.id} data-item-id={basketItem.id}>
                      <AfterSalesLabourBasketItem
                        item={basketItem}
                        onSelect={onSelectBasketItem}
                        onQuantityChange={props.onQuantityChange}
                        onEdit={props.onEdit}
                        onDelete={props.onDelete}
                        onTabOut={props.onTabOut}
                        hasPendingRequest={props.basket.hasPendingRequest}
                        data-testid={suffixTestId(`basketItem-[${index}]`, props)}
                      />
                    </div>
                  ))}
                </DataStatus>
              </VStack>
            </Scroll>

            <Separator />

            <BasketSummary
              totalPrice={basketTotalPrice}
              data-testid={suffixTestId('summary', props)}
            />
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
